<template>
  <v-card color="#1F2937">
    <v-card-title class="white--text">
      {{ isEditing ? "Dauerauftrag bearbeiten" : "Neue Ausgabe erfassen" }}
    </v-card-title>
    <v-card-text class="white--text">
      <v-form ref="expenseForm" v-model="valid" @submit.prevent="save">
        <v-text-field v-model="expense.title" label="Titel / Zweck" :rules="[rules.required]" dark outlined dense></v-text-field>

        <v-text-field
          v-model.number="expense.amount"
          label="Betrag (CHF)"
          type="number"
          :rules="[rules.required, rules.positive]"
          dark
          outlined
          dense></v-text-field>

        <v-autocomplete
          v-model="expense.category"
          :items="categories"
          label="Kategorie"
          :rules="[rules.required]"
          dark
          outlined
          dense></v-autocomplete>

        <v-row no-gutters>
          <v-col v-if="expense.category === 'Personalaufwand'" cols="6">
            <v-switch v-model="expense.isWage" label="Lohn" dark color="primary"></v-switch>
          </v-col>
          <v-col cols="6">
            <v-switch :disabled="isEditing" v-model="expense.isRecurring" label="Dauerauftrag" dark color="primary"></v-switch>
          </v-col>
        </v-row>

        <v-menu
          v-if="!expense.isRecurring"
          v-model="menus.singleDate"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="expense.date"
              label="Datum der Ausgabe"
              prepend-inner-icon="mdi-calendar"
              :rules="!expense.isRecurring ? [rules.required] : []"
              readonly
              dark
              outlined
              dense
              v-bind="attrs"
              v-on="on"></v-text-field>
          </template>
          <v-date-picker v-model="expense.date" @input="menus.singleDate = false" no-title></v-date-picker>
        </v-menu>

        <v-card v-if="expense.isRecurring" outlined class="pa-4 mb-4" color="rgba(255,255,255,0.05)">
          <v-select
            v-model="expense.recurrence.interval"
            :items="intervalOptions"
            item-text="text"
            item-value="value"
            label="Intervall"
            :rules="[rules.required]"
            dark
            outlined
            dense></v-select>

          <v-menu v-model="menus.startDate" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="expense.recurrence.startDate"
                label="Startdatum"
                prepend-inner-icon="mdi-calendar"
                :rules="[rules.required]"
                readonly
                dark
                outlined
                dense
                v-bind="attrs"
                v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="expense.recurrence.startDate" @input="menus.startDate = false" no-title></v-date-picker>
          </v-menu>

          <v-radio-group v-model="endCondition" dark row>
            <v-radio label="Endet am Datum" value="endDate"></v-radio>
            <v-radio label="Endet nach Anzahl" value="occurrences"></v-radio>
            <v-radio label="Läuft unbegrenzt" value="unlimited"></v-radio>
          </v-radio-group>

          <v-menu
            v-if="endCondition === 'endDate'"
            v-model="menus.endDate"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="expense.recurrence.endDate"
                label="Enddatum"
                prepend-inner-icon="mdi-calendar"
                :rules="endCondition === 'endDate' ? [rules.required] : []"
                readonly
                dark
                outlined
                dense
                clearable
                v-bind="attrs"
                v-on="on"></v-text-field>
            </template>
            <v-date-picker v-model="expense.recurrence.endDate" @input="menus.endDate = false" no-title></v-date-picker>
          </v-menu>

          <v-text-field
            v-if="endCondition === 'occurrences'"
            v-model.number="expense.recurrence.occurrences"
            label="Anzahl Ausführungen"
            type="number"
            :rules="endCondition === 'occurrences' ? [rules.required, rules.positive] : []"
            dark
            outlined
            dense></v-text-field>
        </v-card>

        <v-autocomplete
          v-model="expense.studio"
          :items="studios"
          item-text="name"
          item-value="_id"
          label="Studio"
          :rules="[rules.required]"
          placeholder="Studio auswählen"
          dark
          outlined
          dense></v-autocomplete>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn text @click="$emit('close')" class="white--text">Abbrechen</v-btn>
      <v-btn color="primary" :disabled="!valid || loading" :loading="loading" @click="save"> Speichern </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import api from "@/api"

export default {
  name: "ExpenseCaptureForm",
  props: {
    studios: { type: Array, default: () => [] },
    invoiceIssuers: { type: Array, default: () => [] },
    initialData: { type: Object, default: null },
  },
  data() {
    return {
      valid: false,
      loading: false,
      menus: {
        singleDate: false,
        startDate: false,
        endDate: false,
      },
      endCondition: "endDate", // Standard-Auswahl für Radio-Buttons
      intervalOptions: [
        { text: "Wöchentlich", value: "weekly" },
        { text: "Monatlich", value: "monthly" },
        { text: "Quartalsweise", value: "quarterly" },
        { text: "Jährlich", value: "yearly" },
      ],
      categories: [
        "Leistungen Dritter",
        "Personalaufwand",
        "RAUMAUFWAND",
        "ENERGIE- UND ENTSORGUNGSAUFWAND",
        "SACHVERSICH., ABGABEN, GEBÜHREN, BEWILLIGUNGEN",
        "FAHRZEUG- UND TRANSPORTAUFWAND",
        "WERBEAUFWAND",
        "Reisespesen/Kundenbetreuung",
        "Reparaturen / Wartungen",
        "VERWALTUNGS-AUFWAND",
        "INFORMATIKAUFWAND",
        "Beratungskosten",
        "Lizenzen",
        "ÜBRIGER BETRIEBSAUFWAND",
        "Forderungsverluste",
        "Fitnessgeräte und Solarien",
        "Sauna / Dampfbad",
        "Handelswaren",
        "Leasing Maschinen",
        "Steuern",
        "Mobilien und Einrichtungen",
        "Büromobiliar/Büro-/EDV-Geräte",
        "Fahrzeuge",
        "Unbekannt",
      ],
      expense: {
        title: "",
        amount: null,
        isWage: false,
        category: "",
        date: new Date().toISOString().substr(0, 10),
        studio: null,
        isRecurring: false,
        recurrence: {
          interval: "monthly",
          startDate: new Date().toISOString().substr(0, 10),
          endDate: null,
          occurrences: null,
        },
      },
      rules: {
        required: (v) => !!v || "Dieses Feld ist erforderlich.",
        positive: (v) => v > 0 || "Der Betrag muss positiv sein.",
      },
    }
  },
  computed: {
    isEditing() {
      return this.initialData && this.initialData._id
    },
  },
  created() {
    if (this.isEditing) {
      // Formular-Daten mit den übergebenen Werten füllen (tiefe Kopie)

      this.expense = JSON.parse(JSON.stringify(this.initialData))

      // Das Studio-Objekt enthält den Namen, wir brauchen aber nur die ID für v-model
      if (this.expense.studio && typeof this.expense.studio === "object") {
        this.expense.studio = this.expense.studio._id
      }

      // Datumsfelder für die Date-Picker korrekt formatieren (YYYY-MM-DD)
      if (this.expense.date) {
        this.expense.date = new Date(this.expense.date).toISOString().substr(0, 10)
      }
      if (this.expense.recurrence?.startDate) {
        this.expense.recurrence.startDate = new Date(this.expense.recurrence.startDate).toISOString().substr(0, 10)
      }
      if (this.expense.recurrence?.endDate) {
        this.expense.recurrence.endDate = new Date(this.expense.recurrence.endDate).toISOString().substr(0, 10)
      }

      // End-Bedingung für die Radio-Buttons korrekt setzen
      if (this.expense.isRecurring) {
        if (this.expense.recurrence.endDate) this.endCondition = "endDate"
        else if (this.expense.recurrence.occurrences) this.endCondition = "occurrences"
        else this.endCondition = "unlimited"
      }
    }
  },
  watch: {
    // Sorgt dafür, dass immer nur eine End-Bedingung gesetzt ist
    endCondition(newVal) {
      if (!this.expense.recurrence) return
      if (newVal === "endDate") {
        this.expense.recurrence.occurrences = null
      } else if (newVal === "occurrences") {
        this.expense.recurrence.endDate = null
      } else {
        // 'unlimited'
        this.expense.recurrence.occurrences = null
        this.expense.recurrence.endDate = null
      }
    },
  },
  methods: {
    async save() {
      if (!this.valid) return
      this.loading = true
      try {
        // Unterscheiden zwischen dem Erstellen und Aktualisieren
        if (this.isEditing) {
          await api.expenses.update(this.expense._id, this.expense)
        } else {
          await api.expenses.create(this.expense)
        }
        this.$emit("expense-saved") // Signal an Parent senden
      } catch (error) {
        console.error("Fehler beim Speichern der Ausgabe:", error)
        this.$toast.error("Ausgabe konnte nicht gespeichert werden.")
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
